import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, PieChart, Pie, LabelList,
} from "recharts";
import DownloadableChartCard from "./DownloadableChartCard";

/* -------------------------------------------------------------------------
   Publish-state pill — reused on listing + drill-down.
   --------------------------------------------------------------------- */
const STATE_STYLES = {
  pending:    "bg-amber-100   text-amber-800   border-amber-300",
  published:  "bg-emerald-100 text-emerald-800 border-emerald-300",
  held:       "bg-neutral-200 text-neutral-700 border-neutral-300",
  mixed:      "bg-violet-100  text-violet-800  border-violet-300",
  no_results: "bg-red-100     text-red-700     border-red-300",
};
const STATE_LABEL = {
  pending: "PENDING", published: "PUBLISHED", held: "HELD",
  mixed: "MIXED", no_results: "NO RESULTS",
};

export function StateBadge({ state }) {
  const klass = STATE_STYLES[state] || "bg-neutral-100 text-neutral-600 border-neutral-300";
  return (
    <span data-testid={`state-${state}`} className={`inline-block px-2 py-0.5 text-[11px] font-bold tracking-wider border-2 ${klass}`}>
      {STATE_LABEL[state] || (state || "").toUpperCase()}
    </span>
  );
}

/* -------------------------------------------------------------------------
   LISTING PAGE  —  /admin/survey-results
   --------------------------------------------------------------------- */
const TAB_OPTIONS = [
  { key: "",          label: "All" },
  { key: "pending",   label: "Pending" },
  { key: "published", label: "Published" },
  { key: "held",      label: "Held" },
  { key: "mixed",     label: "Mixed" },
];

export function AdminSurveyResults() {
  const [data, setData] = useState({ items: [], summary: {} });
  const [tab, setTab] = useState("");
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState(null); // { wid, action, name }
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (tab) params.set("state", tab);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/admin/survey-results${params.toString() ? `?${params}` : ""}`)
      .then((r) => setData(r.data || { items: [], summary: {} }))
      .catch(() => toast.error("Failed to load survey results"));
  };
  useEffect(() => { load(); }, [tab, q]);  // eslint-disable-line react-hooks/exhaustive-deps

  const performAction = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.post(`/admin/survey-results/${confirm.wid}/${confirm.action}`);
      const verb = confirm.action === "publish" ? "Published" : confirm.action === "hold" ? "Held" : "Unpublished";
      toast.success(`${verb}: ${confirm.name}`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const counts = data.summary || {};
  return (
    <div className="space-y-5" data-testid="admin-survey-results">
      <div className="border-b-2 border-black pb-4">
        <div className="overline">SURVEY RESULTS</div>
        <h1 className="text-4xl font-black tracking-tighter">Publish gate</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
          Closed-wave results sit in <strong>Pending</strong> until you publish them. Held results are stored
          in the DB (and exported to enterprise customers if commissioned) but never appear on the public
          site, sector pages or patron app.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        {["all", "pending", "published", "held"].map((k) => (
          <div key={k} className="border-2 border-black p-3 bg-white">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">{k}</div>
            <div className="text-3xl font-black mt-0.5" data-testid={`kpi-${k}`}>{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1">
          {TAB_OPTIONS.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key || "all"}`}
              className={`px-3 py-1.5 text-xs font-bold border-2 ${tab === t.key ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
            >
              {t.label}{t.key ? ` (${counts[t.key] || 0})` : ""}
            </button>
          ))}
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search wave by name…"
          className="max-w-xs border-2 border-black rounded-none"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      <div className="border-2 border-black bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="results-table">
          <thead className="border-b-2 border-black bg-neutral-50 text-left text-xs uppercase tracking-wider">
            <tr>
              <th className="p-2.5 px-3">Wave</th>
              <th className="p-2.5 px-3">Sector</th>
              <th className="p-2.5 px-3">Closed</th>
              <th className="p-2.5 px-3 text-right">n_real</th>
              <th className="p-2.5 px-3">State</th>
              <th className="p-2.5 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-neutral-500">No closed waves match.</td></tr>
            )}
            {data.items.map((row) => (
              <tr key={row.wave_id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="p-2.5 px-3 font-bold">
                  <Link to={`/admin/survey-results/${row.wave_id}`} className="hover:underline" data-testid={`open-${row.wave_id}`}>
                    {row.name}
                  </Link>
                </td>
                <td className="p-2.5 px-3 font-mono text-xs text-neutral-600">{row.sector_key || "—"}</td>
                <td className="p-2.5 px-3 text-neutral-600 text-xs">{(row.closed_at || "").slice(0, 10) || "—"}</td>
                <td className="p-2.5 px-3 text-right tabular-nums">{row.n_real || 0}</td>
                <td className="p-2.5 px-3"><StateBadge state={row.publish_state} /></td>
                <td className="p-2.5 px-3 text-right">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {(row.publish_state === "pending" || row.publish_state === "held" || row.publish_state === "mixed") && (
                      <Button size="sm" onClick={() => setConfirm({ wid: row.wave_id, action: "publish", name: row.name })} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none h-8 px-2.5 text-xs" data-testid={`publish-${row.wave_id}`}>Publish</Button>
                    )}
                    {row.publish_state === "published" && (
                      <Button size="sm" variant="outline" onClick={() => setConfirm({ wid: row.wave_id, action: "unpublish", name: row.name })} className="rounded-none h-8 px-2.5 text-xs" data-testid={`unpublish-${row.wave_id}`}>Unpublish</Button>
                    )}
                    {row.publish_state !== "held" && (
                      <Button size="sm" variant="outline" onClick={() => setConfirm({ wid: row.wave_id, action: "hold", name: row.name })} className="rounded-none h-8 px-2.5 text-xs" data-testid={`hold-${row.wave_id}`}>Hold</Button>
                    )}
                    <Link to={`/admin/survey-results/${row.wave_id}`} className="text-xs underline self-center">View</Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-md rounded-none border-2 border-black" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle className="text-2xl font-black tracking-tight">
            {confirm?.action === "publish" && "Publish results?"}
            {confirm?.action === "hold" && "Hold results?"}
            {confirm?.action === "unpublish" && "Unpublish results?"}
          </DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <div className="font-bold">{confirm?.name}</div>
            {confirm?.action === "publish" && (
              <p className="text-neutral-600">Results will become live on the frontend immediately, gated by each question&apos;s visibility tier (public / panelists / subscribers / enterprise / private). This is reversible — you can Hold or Unpublish at any time.</p>
            )}
            {confirm?.action === "hold" && (
              <p className="text-neutral-600">Results will be hidden from every viewer (public, patrons, subscribers, enterprise). The raw data stays in the database — enterprise customers with commissioned waves still receive their CSV exports.</p>
            )}
            {confirm?.action === "unpublish" && (
              <p className="text-neutral-600">Live results will be hidden from the frontend immediately. The wave moves to Held state. Republish at any time to bring them back.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} className="rounded-none">Cancel</Button>
            <Button onClick={performAction} disabled={busy} className="bg-black hover:bg-neutral-800 text-white rounded-none" data-testid="confirm-action">
              {busy ? "Working…" : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------------------------------
   DRILL-DOWN PAGE  —  /admin/survey-results/:wid
   --------------------------------------------------------------------- */
// Brand palette: mango-green anchor + high-contrast complementary hues that
// stay readable in both light and dark modes. Kept short so identical values
// never neighbour each other on a 3–6 option pie.
const CHART_COLORS = [
  "#16A34A", // brand mango-green
  "#0EA5E9", // sky
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#0F172A", // slate near-black
  "#14B8A6", // teal
  "#F97316", // orange
];

// Per-question retro visibility picker. Patches snapshot/tracker result
// rows (+ the source question) INSTANTLY without re-running ETL. See
// `PATCH /api/admin/survey-results/{wid}/questions/{qid}/visibility`.
const VIS_META = {
  public:      { label: "Public",      cls: "bg-emerald-600 text-white" },
  panelists:   { label: "Panelists",   cls: "bg-sky-600 text-white" },
  subscribers: { label: "Subscribers", cls: "bg-violet-600 text-white" },
  enterprise:  { label: "Enterprise",  cls: "bg-black text-white" },
  private:     { label: "Private",     cls: "bg-red-700 text-white" },
};
function QuestionVisibilityPicker({ wid, qid, value, disabled, onSaved }) {
  const [v, setV] = React.useState(value || "public");
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setV(value || "public"); }, [value]);
  const change = async (next) => {
    if (next === v) return;
    setSaving(true);
    const prev = v;
    setV(next);   // optimistic
    try {
      await api.patch(`/admin/survey-results/${wid}/questions/${qid}/visibility`, { visibility: next });
      toast.success(`Visibility → ${VIS_META[next]?.label || next}`);
      onSaved && onSaved();
    } catch (e) {
      setV(prev);
      toast.error(e.response?.data?.detail || "Failed to change visibility");
    } finally {
      setSaving(false);
    }
  };
  const meta = VIS_META[v] || VIS_META.public;
  return (
    <div className="flex items-center gap-2" data-testid={`q-vis-${qid}`}>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${meta.cls}`}>
        {meta.label}
      </span>
      <select
        value={v}
        disabled={disabled || saving}
        onChange={(e) => change(e.target.value)}
        className="text-[11px] border border-neutral-300 rounded-none px-1 py-0.5 bg-white hover:border-black disabled:bg-neutral-100 disabled:text-neutral-400"
        data-testid={`q-vis-select-${qid}`}
        title="Change who sees this question's results (takes effect immediately, no ETL re-run needed)"
      >
        {Object.keys(VIS_META).map((k) => (
          <option key={k} value={k}>{VIS_META[k].label}</option>
        ))}
      </select>
    </div>
  );
}

// Inline percentage label rendered ON each pie slice (large mango number, no
// hover required). Skipped for tiny slices (< 5%) to avoid overlap smear —
// those still get their percentage in the legend row below the chart.
function PieSliceLabel({ cx, cy, midAngle, innerRadius, outerRadius, value }) {
  if (value == null || value < 5) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.58;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
          fontSize={12} fontWeight={700} style={{ pointerEvents: "none",
            textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
      {`${Math.round(value)}%`}
    </text>
  );
}

// Themed tooltip — "Label — 34%" on one line, nothing else. Consistent across
// pie / bar / line so the whole results deck feels of a piece.
function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const label = p.payload?.label ?? p.name;
  const pct = p.value;
  return (
    <div className="bg-neutral-900 text-white px-3 py-1.5 border border-neutral-800 shadow-lg text-xs font-medium">
      <span className="opacity-90">{label}</span>
      <span className="mx-1.5 opacity-60">—</span>
      <span className="font-bold tabular-nums">{typeof pct === "number" ? `${Math.round(pct * 10) / 10}%` : pct}</span>
    </div>
  );
}

// Compact legend row for the pie — colour square + label + percentage —
// so every slice's actual number is visible without hovering (per the
// request: "the figure should appear in a nice small font even without
// hovering"). Uses a 2-column grid on wide screens to stay tidy.
function PieLegend({ data }) {
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
      {data.map((d, i) => (
        <div key={d.label} className="flex items-center gap-2 min-w-0">
          <span className="w-3 h-3 shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
          <span className="truncate text-neutral-700" title={d.label}>{d.label}</span>
          <span className="ml-auto font-bold tabular-nums text-neutral-900">{Math.round(d.pct * 10) / 10}%</span>
        </div>
      ))}
    </div>
  );
}

function QuestionChart({ item }) {
  const data = item.chart || [];
  if (!data.length) return <div className="text-neutral-500 text-sm py-8">No chart data — ETL may not have run yet.</div>;
  const ct = item.chart_type || "bar";
  if (ct === "pie" || ct === "donut") {
    return (
      <div>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={data}
              dataKey="pct"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={ct === "donut" ? 62 : 0}
              outerRadius={110}
              paddingAngle={1}
              stroke="#fff"
              strokeWidth={2}
              label={PieSliceLabel}
              labelLine={false}
              isAnimationActive={false}
            >
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <PieLegend data={data} />
      </div>
    );
  }
  if (ct === "line" || ct === "area") {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 12, right: 20, left: 0, bottom: 4 }}>
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252" }} axisLine={{ stroke: "#e5e5e5" }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#525252" }} axisLine={{ stroke: "#e5e5e5" }} tickLine={false} tickFormatter={(v) => `${v}%`} />
          <Tooltip content={<ChartTooltip />} />
          <Line type="monotone" dataKey="pct" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3, fill: "#16A34A" }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  // Default: horizontal bar. Inline % label on each bar (visible without
  // hovering), thin single-tone grid lines, no chart border.
  return (
    <ResponsiveContainer width="100%" height={Math.max(200, data.length * 42 + 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 44, left: 0, bottom: 4 }}>
        <XAxis type="number" hide domain={[0, 100]} />
        <YAxis
          dataKey="label"
          type="category"
          width={150}
          tick={{ fontSize: 11, fill: "#525252" }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
        <Bar dataKey="pct" radius={[0, 3, 3, 0]} barSize={18} isAnimationActive={false}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          <LabelList
            dataKey="pct"
            position="right"
            formatter={(v) => `${Math.round(v * 10) / 10}%`}
            style={{ fontSize: 11, fill: "#111", fontWeight: 700 }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function AdminSurveyResultsDetail() {
  const { wid } = useParams();
  const nav = useNavigate();
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/survey-results/${wid}`).then((r) => setDetail(r.data)).catch(() => toast.error("Failed to load wave"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [wid]);  // eslint-disable-line react-hooks/exhaustive-deps

  const performAction = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.post(`/admin/survey-results/${wid}/${confirm}`);
      toast.success(`Wave ${confirm}${confirm.endsWith("e") ? "d" : "ed"}`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!detail) return <div className="p-8 text-neutral-500">Loading…</div>;

  // Aggregate publish state across items for the action panel.
  const states = new Set(detail.items.map((it) => it.publish_state).filter(Boolean));
  const aggState = states.size === 0 ? "no_results" : states.size === 1 ? [...states][0] : "mixed";
  const closed = (detail.closed_at || "").slice(0, 10) || "—";

  return (
    <div className="space-y-5" data-testid="survey-results-detail">
      <button onClick={() => nav("/admin/survey-results")} className="text-sm underline" data-testid="back-link">← All survey results</button>

      <div className="border-b-2 border-black pb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">SURVEY RESULTS · {detail.sector_key || "—"}</div>
          <h1 className="text-4xl font-black tracking-tighter">{detail.name}</h1>
          <div className="text-xs text-neutral-600 mt-1.5">
            Closed {closed} · n_real {detail.items[0]?.n_sample ?? 0} of target {detail.target_sample || "—"} ·
            {" "}Questions: {detail.items.length} · Visibility:
            {" "}{Object.entries(detail.visibility_summary || {}).map(([k, v]) => `${v} ${k}`).join(" · ") || "—"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StateBadge state={aggState} />
          <div className="flex flex-wrap justify-end gap-1.5">
            {aggState !== "published" && (
              <Button onClick={() => setConfirm("publish")} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none" data-testid="action-publish">Publish</Button>
            )}
            {aggState === "published" && (
              <Button variant="outline" onClick={() => setConfirm("unpublish")} className="rounded-none" data-testid="action-unpublish">Unpublish</Button>
            )}
            {aggState !== "held" && (
              <Button variant="outline" onClick={() => setConfirm("hold")} className="rounded-none" data-testid="action-hold">Hold</Button>
            )}
            <Button variant="outline" onClick={() => setConfirm("recompute")} className="rounded-none" data-testid="action-recompute" title="Re-run the aggregation from raw responses — use this if a percentage looks wrong without changing publish state">Recompute</Button>
          </div>
        </div>
      </div>

      <ResultFiltersCard wid={wid} detail={detail} onSaved={load} />

      <DisplayNOverrideCard wid={wid} detail={detail} onSaved={load} />

      <WaveDisclaimerCard wid={wid} detail={detail} onSaved={load} />

      <div className="grid lg:grid-cols-2 gap-4">
        {detail.items.map((it) => (
          <DownloadableChartCard
            key={it.question_id}
            title={it.question_text}
            filenameBase={`q-${(it.question_text || "question").slice(0, 40)}`}
            className="border-2 border-black"
            bodyClassName="p-4"
          >
            <div data-testid={`q-${it.question_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <StateBadge state={it.publish_state || "no_results"} />
                <QuestionVisibilityPicker
                  wid={wid}
                  qid={it.question_id}
                  value={it.visibility || "public"}
                  disabled={!it.publish_state}
                  onSaved={load}
                />
              </div>
              <QuestionChart item={it} />
            </div>
          </DownloadableChartCard>
        ))}
      </div>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-md rounded-none border-2 border-black">
          <DialogHeader><DialogTitle className="text-2xl font-black tracking-tight">
            {confirm === "publish" && "Publish all results?"}
            {confirm === "hold" && "Hold all results?"}
            {confirm === "unpublish" && "Unpublish all results?"}
            {confirm === "recompute" && "Recompute all results?"}
          </DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-600">
            {confirm === "recompute"
              ? "Re-runs the aggregation from the raw responses for every question in this wave. Publish state is preserved — this only rebuilds the numbers."
              : `${detail.items.length} question result${detail.items.length === 1 ? "" : "s"} will be updated.`}
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} className="rounded-none">Cancel</Button>
            <Button onClick={performAction} disabled={busy} className="bg-black hover:bg-neutral-800 text-white rounded-none" data-testid="detail-confirm">{busy ? "Working…" : "Confirm"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * ITER 63 — Public disclaimer editor for a closed wave's snapshot archive.
 * Shown on /snapshots/wave/{wid} under the wave title (mango-bordered
 * italic block). Editable on ANY wave status because the field is
 * whitelisted in cms.py `presentation_only`. Same PATCH endpoint as intro
 * text — but surfaced HERE so admins can add it right where they're
 * looking at the results.
 */
function WaveDisclaimerCard({ wid, detail, onSaved }) {
  const initial = detail.wave_disclaimer || "";
  const [value, setValue] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  // Only reset the textarea when the parent refetches AND the admin has no
  // in-flight edits — otherwise a background poll would silently discard
  // the unsaved draft. `dirty` is derived from the initial value pulled at
  // last save-time, not from the current parent detail (which may lag).
  const [lastSaved, setLastSaved] = React.useState(initial);
  React.useEffect(() => {
    if (value === lastSaved) {
      setValue(detail.wave_disclaimer || "");
      setLastSaved(detail.wave_disclaimer || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.wave_disclaimer]);
  const dirty = value !== lastSaved;
  const save = async () => {
    setSaving(true);
    try {
      const trimmed = value.trim();
      await api.patch(`/waves/${wid}`, { wave_disclaimer: trimmed || null });
      setLastSaved(trimmed);
      toast.success(trimmed ? "Disclaimer saved" : "Disclaimer cleared");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const clear = async () => {
    setSaving(true);
    try {
      await api.patch(`/waves/${wid}`, { wave_disclaimer: null });
      toast.success("Disclaimer cleared");
      setValue("");
      setLastSaved("");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-white border-2 border-neutral-300 p-4" data-testid="wave-disclaimer-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="min-w-0 flex-1">
          <div className="overline text-neutral-500">SNAPSHOT ARCHIVE DISCLAIMER (PUBLIC)</div>
          <div className="text-xs text-neutral-500 mt-1">
            Rendered under the wave title on <code>/snapshots/wave/{wid.slice(0, 8)}…</code> — public archive page.
            Use for a methodology note, sample limitation, or a client-facing caveat.
            Multi-line supported. Leave blank to hide.
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={clear}
            disabled={saving || !initial}
            className="rounded-none text-xs"
            data-testid="wave-disclaimer-clear"
          >
            Clear
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-black hover:bg-neutral-800 text-white rounded-none"
            data-testid="wave-disclaimer-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={4}
        className="w-full border-2 border-neutral-300 px-3 py-2 text-sm rounded-none focus:border-black"
        placeholder="e.g. Results reflect self-reported attitudes at the moment of survey. Sample composition may skew urban. Percentages may not sum to 100% due to rounding."
        data-testid="wave-disclaimer-input"
      />
    </div>
  );
}


/**
 * Admin-only inline card for controlling how the wave's sample size (N) is
 * displayed publicly. Unified single-toggle model:
 *   • Toggle OFF → N is hidden entirely from public views.
 *   • Toggle ON  → N is shown. If a custom value is entered, that value is
 *     shown instead of the true N (must be ≥ true N).
 * Backend: patches `show_respondent_count` (visibility gate) + optional
 * `show_n_sample_override` / `n_sample_override_value` (custom-N swap).
 */
function DisplayNOverrideCard({ wid, detail, onSaved }) {
  const trueN = detail.items[0]?.n_sample ?? 0;
  const initialShow = !!detail.show_respondent_count;
  const initialValue = detail.n_sample_override_value ?? "";
  const [show, setShow] = React.useState(initialShow);
  const [value, setValue] = React.useState(initialValue);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    setShow(!!detail.show_respondent_count);
    setValue(detail.n_sample_override_value ?? "");
  }, [detail.show_respondent_count, detail.n_sample_override_value]);
  const dirty = show !== initialShow || String(value ?? "") !== String(initialValue ?? "");
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const raw = String(value ?? "").trim();
      const parsed = raw === "" ? null : parseInt(raw, 10);
      const hasCustom = show && parsed !== null && !Number.isNaN(parsed);
      await api.patch(`/waves/${wid}`, {
        show_respondent_count: show,
        show_n_sample_override: hasCustom,
        n_sample_override_value: hasCustom ? parsed : null,
      });
      toast.success(show ? (hasCustom ? "Public N override saved" : "True N will be shown publicly") : "Sample size hidden from public");
      onSaved && onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-white border-2 border-neutral-300 p-4" data-testid="display-n-override-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="overline text-neutral-500">SAMPLE SIZE DISPLAY</div>
          <div className="text-sm text-neutral-700 mt-1">
            True sample size (admin view): <span className="font-mono font-bold">{trueN}</span>
            {show && (
              value !== "" && Number(value) >= trueN
                ? <span className="text-neutral-500"> · public sees <span className="font-mono font-bold text-mango">{value}</span></span>
                : <span className="text-neutral-500"> · public sees <span className="font-mono font-bold text-mango">{trueN}</span> (true N)</span>
            )}
            {!show && <span className="text-neutral-500"> · <span className="font-bold text-red-600">hidden from public</span></span>}
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            One toggle governs the whole N line. Enter a custom value only if you want a rounded / representative-of-target
            figure shown to viewers (must be ≥ true N). Admin & researcher views always see the true count.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider cursor-pointer">
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
              className="w-4 h-4"
              data-testid="toggle-n-override"
            />
            Show sample size
          </label>
          <input
            type="number"
            min={trueN}
            placeholder={`Custom N (min ${trueN}) — optional`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!show}
            className="w-56 border-2 border-neutral-300 px-2 py-1 text-sm rounded-none focus:border-black disabled:bg-neutral-100 disabled:text-neutral-400"
            data-testid="input-n-override"
          />
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-black hover:bg-neutral-800 text-white rounded-none"
            data-testid="save-n-override"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
    </div>
  );
}

/**
 * Admin card for slicing published results by demographic filters
 * (geo_scope / age_band / gender at MVP). Saving triggers a server-side
 * re-run of ETL; tracker_results + snapshot_results are rewritten in place
 * with the filtered aggregation, preserving publish_state.
 */
function ResultFiltersCard({ wid, detail, onSaved }) {
  const FIELD_LABELS = { geo: "Geography", region: "Region / Province", age_band: "Age band", gender: "Gender" };
  // Pretty labels for individual filter values (`pk` → "Pakistan", etc.).
  const VALUE_LABELS = {
    geo: { pk: "Pakistan", overseas: "Overseas" },
  };
  const prettyValue = (field, v) => (VALUE_LABELS[field] && VALUE_LABELS[field][v]) || v;
  const opts = detail.filter_slice_options || {};
  const initial = detail.result_filters || {};
  const [filters, setFilters] = React.useState(initial);
  const [preview, setPreview] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => { setFilters(detail.result_filters || {}); }, [detail.result_filters]);
  React.useEffect(() => {
    let cancel = false;
    api.post(`/admin/survey-results/${wid}/filters/preview`, { result_filters: filters })
      .then((r) => { if (!cancel) setPreview(r.data); })
      .catch(() => { if (!cancel) setPreview(null); });
    return () => { cancel = true; };
  }, [wid, filters]);
  const dirty = JSON.stringify(filters || {}) !== JSON.stringify(initial || {});
  const toggle = (field, val) => {
    setFilters((prev) => {
      const cur = new Set(prev[field] || []);
      if (cur.has(val)) cur.delete(val); else cur.add(val);
      const next = { ...prev };
      if (cur.size === 0) delete next[field]; else next[field] = [...cur];
      return next;
    });
  };
  const reset = () => setFilters({});
  const save = async (force = false) => {
    setSaving(true); setErr(null);
    try {
      await api.post(`/admin/survey-results/${wid}/filters`, { result_filters: filters, force });
      toast.success("Filters saved · ETL re-run · charts updated");
      onSaved && onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      setErr(msg);
      // Below-floor case → offer override
      if (msg.toLowerCase().includes("below the minimum") && window.confirm(`${msg}\n\nOverride and save anyway?`)) {
        return save(true);
      }
      toast.error(msg);
    } finally { setSaving(false); }
  };
  const hasAnyOptions = Object.values(opts).some((arr) => (arr || []).length > 0);
  return (
    <div className="bg-white border-2 border-neutral-300 p-4" data-testid="result-filters-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="overline text-neutral-500">RESULT FILTERS</div>
          <div className="text-xs text-neutral-500 mt-1">
            Choose which slices of your respondents to include in the published aggregations.
            Saving re-runs ETL and rewrites the charts in place.
          </div>
        </div>
        <div className="text-right">
          {preview && (
            <div className="text-sm">
              With current filters: <span className="font-mono font-bold text-mango">n={preview.filtered_n}</span>
              <span className="text-neutral-500"> (of {preview.true_n})</span>
            </div>
          )}
        </div>
      </div>
      {!hasAnyOptions ? (
        <div className="text-sm text-neutral-500 italic py-2">No demographic slices collected for this wave — nothing to filter.</div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4">
          {Object.entries(FIELD_LABELS).map(([field, label]) => {
            const values = opts[field] || [];
            const selected = filters[field] || [];
            if (!values.length) return null;
            return (
              <div key={field}>
                <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">{label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {values.map((v) => {
                    const on = selected.includes(v);
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => toggle(field, v)}
                        data-testid={`filter-${field}-${v}`}
                        className={`px-2 py-1 text-xs border-2 rounded-none transition-colors ${
                          on ? "bg-mango text-white border-mango" : "bg-white text-neutral-700 border-neutral-300 hover:border-black"
                        }`}
                      >
                        {prettyValue(field, v)}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-neutral-400 mt-1">
                  {selected.length === 0 ? "All values included" : `${selected.length} of ${values.length} selected`}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 mt-4 border-t pt-3">
        {dirty && (
          <button onClick={reset} className="text-xs underline text-neutral-500 hover:text-black" data-testid="filter-reset">
            Reset to all
          </button>
        )}
        <Button
          onClick={() => save(false)}
          disabled={!dirty || saving}
          className="bg-black hover:bg-neutral-800 text-white rounded-none"
          data-testid="filter-save"
        >
          {saving ? "Re-running ETL…" : "Save & re-run ETL"}
        </Button>
      </div>
      {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
    </div>
  );
}
